/**
 * AES helper equivalent to the backend Java `AESUtils`.
 *
 * Backend reference (com.aeromexico.pc.payment.revenue.dc.util.AESUtils):
 *   - Algorithm: AES/CBC/PKCS5Padding
 *   - IV: 16 zero bytes (fixed, NOT random)
 *   - Key: Base64 encoded, decoded to raw bytes (AES-256 in current envs)
 *   - Cipher text: Base64 string
 *   - Plain text: UTF-8 JSON string (PaymentRevenueRequest)
 *
 * PKCS5Padding on 16-byte blocks is equivalent to PKCS7, which is what
 * Web Crypto uses internally for AES-CBC, so the two sides are compatible.
 *
 * NOTE: The backend uses a fixed all-zero IV. We must replicate it to stay
 * compatible, even though a random IV would be more secure.
 */

const ALGORITHM = 'AES-CBC';
const IV = new Uint8Array(16); // 16 zero bytes, matches `new byte[16]` in Java

// Default secret key for the tc/reservation payload, provided via env.
const DEFAULT_SECRET_KEY = process.env.REACT_APP_EC_TC;

function resolveKey(secretKeyBase64) {
  const key = secretKeyBase64 || DEFAULT_SECRET_KEY;
  if (!key) {
    throw new Error(
      'Missing AES secret key. Set REACT_APP_EC_TC or pass a key explicitly.'
    );
  }
  return key;
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i += 1) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
}

async function importKey(secretKeyBase64) {
  const rawKey = base64ToBytes(secretKeyBase64);
  return crypto.subtle.importKey('raw', rawKey, { name: ALGORITHM }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Encrypts a plain string (typically JSON) into a Base64 cipher text that the
 * Java backend can decrypt with `AESUtils.decryptToString`.
 *
 * @param {string} plainText - UTF-8 string to encrypt.
 * @param {string} [secretKeyBase64] - Base64 encoded AES key. Defaults to REACT_APP_EC_TC.
 * @return {Promise<string>} Base64 encoded cipher text.
 */
export async function encrypt(plainText, secretKeyBase64) {
  const key = await importKey(resolveKey(secretKeyBase64));
  const data = new TextEncoder().encode(plainText);
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: IV },
    key,
    data
  );
  return bytesToBase64(cipherBuffer);
}

/**
 * Decrypts a Base64 cipher text produced with the same AES/CBC/zero-IV scheme.
 *
 * @param {string} cipherTextBase64 - Base64 encoded cipher text.
 * @param {string} [secretKeyBase64] - Base64 encoded AES key. Defaults to REACT_APP_EC_TC.
 * @return {Promise<string>} Decrypted UTF-8 string.
 */
export async function decrypt(cipherTextBase64, secretKeyBase64) {
  const key = await importKey(resolveKey(secretKeyBase64));
  const cipherBytes = base64ToBytes(cipherTextBase64);
  const plainBuffer = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: IV },
    key,
    cipherBytes
  );
  return new TextDecoder().decode(plainBuffer);
}

/**
 * Convenience helper: encrypts an object as JSON, mirroring how the backend
 * deserializes the decrypted body back into `PaymentRevenueRequest`.
 *
 * @param {object} payload - Object to serialize and encrypt.
 * @param {string} [secretKeyBase64] - Base64 encoded AES key. Defaults to REACT_APP_EC_TC.
 * @return {Promise<string>} Base64 encoded cipher text.
 */
export async function encryptJson(payload, secretKeyBase64) {
  return encrypt(JSON.stringify(payload), secretKeyBase64);
}

/**
 * Decrypts a Base64 cipher text and parses the result as JSON. Mirrors the
 * backend `AESUtils.decryptToString`, which deserializes into PaymentRevenueRequest.
 *
 * @param {string} cipherTextBase64 - Base64 encoded cipher text.
 * @param {string} [secretKeyBase64] - Base64 encoded AES key. Defaults to REACT_APP_EC_TC.
 * @return {Promise<object>} Parsed JSON object.
 */
export async function decryptJson(cipherTextBase64, secretKeyBase64) {
  return JSON.parse(await decrypt(cipherTextBase64, secretKeyBase64));
}

/**
 * Descifra la respuesta cifrada del endpoint `/tc/reservation/v2/pnr` (WEB20-2144)
 * y devuelve el objeto de reservación en JSON.
 *
 * El micro mtpsbk responde en 200 con un texto cifrado (no JSON). Reutiliza el
 * mismo esquema AES/CBC con IV de 16 bytes en cero que replica el backend Java
 * `AESUtils`, con la key por defecto `REACT_APP_EC_TC`.
 *
 * NOTA (P1 del documento técnico): si el equipo de `mtpsbk` define para v2 un
 * esquema distinto (otro algoritmo, IV/salt o llave), este es el único punto a
 * ajustar; el resto del flujo (token PingOne y wiring) no cambia.
 *
 * @param {string} encryptedBase64 - Texto cifrado en Base64 devuelto por el micro.
 * @param {string} [secretKeyBase64] - Key AES en Base64. Default: REACT_APP_EC_TC.
 * @return {Promise<object>} Objeto de reservación ya descifrado y parseado.
 */
export async function decryptReservationPayload(encryptedBase64, secretKeyBase64) {
  return decryptJson(encryptedBase64, secretKeyBase64);
}
